import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import DataTable from "react-data-table-component";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";
import "../assets/CSS/Dashboard.css";

export default function DiePurchaseModule() {

  /* ================= USER ROLE ================= */
  const user = JSON.parse(localStorage.getItem("user")) || {};

  const userRole = user?.Auth || "";
  const userId = user?.EmployeeID || "";

  /* ========= ROLE BASED ========= */

  const isAdminUser = userRole === "Admin";
  const isSuperAdmin = userRole === "SuperAdmin";
  const isViewer = userRole === "Viewer"; // optional future

  const canView   = isAdminUser || isSuperAdmin || userRole === "User";
  const canAdd    = isAdminUser || isSuperAdmin;
  const canEdit   = isAdminUser || isSuperAdmin;
  const canDelete = isSuperAdmin;  // only superadmin can delete
  const canExport = isAdminUser || isSuperAdmin;

  /* ================= SIDEBAR ================= */
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  /* ================= STATE ================= */
  const [activeTab, setActiveTab] = useState("summary");
  const [purchaseList, setPurchaseList] = useState([]);
  const [discardList, setDiscardList] = useState([]);
  const [summary, setSummary] = useState([]);

  const [summarySearch, setSummarySearch] = useState("");
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [discardSearch, setDiscardSearch] = useState("");

  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [formType, setFormType] = useState("purchase");
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    PartyName: "",
    PurchaseDate: "",
    DieCode: "",
    Description: "",
    NumberOfPieces: "",
    Amount: "",
    DiscardDate: ""
  });

  /* ================= LOAD DATA ================= */

  const loadPurchase = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/api/die-purchase?search=${purchaseSearch}`
      );
      setPurchaseList(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadDiscard = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/api/die-discard?search=${discardSearch}`
      );
      setDiscardList(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/api/die-summary?search=${summarySearch}`
      );
      setSummary(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "purchase") {
      loadPurchase();
    } else if (activeTab === "discard") {
      loadDiscard();
    } else {
      loadSummary();
    }
  }, [activeTab, purchaseSearch, discardSearch, summarySearch]);

  /* ================= EXPORT (BACKEND) ================= */

  const handleExport = async () => {
    try {
      const res = await axiosInstance.get(
        "/api/die/export",
        { responseType: "blob" }
      );

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "Die_Inventory_Report.xlsx";
      a.click();

      window.URL.revokeObjectURL(url);

    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Export failed",
        "error"
      );
    }
  };

  /* ================= FORM SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {

      if (formType === "purchase") {

        if (editMode) {
          await axiosInstance.put(
            `/api/die-purchase/${editingId}`,
            formData
          );

          Swal.fire("Updated", "Purchase updated successfully", "success");

        } else {
          await axiosInstance.post("/api/die-purchase", formData);

          Swal.fire("Success", "Purchase recorded successfully", "success");
        }

      } else {

        await axiosInstance.post("/api/die-discard", formData);

        Swal.fire("Success", "Discard recorded successfully", "success");
      }

      resetForm();
      if (activeTab === "purchase") {
        loadPurchase();
      } else if (activeTab === "discard") {
        loadDiscard();
      } else {
        loadSummary();
      }

    } catch (err) {

      Swal.fire(
        "Error",
        err.response?.data?.message || "Operation failed",
        "error"
      );

    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditMode(false);
    setEditingId(null);
    setFormData({
      PartyName: "",
      PurchaseDate: "",
      DieCode: "",
      Description: "",
      NumberOfPieces: "",
      Amount: "",
      DiscardDate: ""
    });
  };

  /* ================= EDIT ================= */

  const handleEdit = (row) => {
    setFormType("purchase");
    setEditMode(true);
    setEditingId(row.PurchaseId);

    const formattedDate = row.PurchaseDate
      ? new Date(row.PurchaseDate).toISOString().split("T")[0]
      : "";

    setFormData({
      ...row,
      PurchaseDate: formattedDate
    });

    setShowModal(true);
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id, type) => {
    if (!canDelete) return;

    Swal.fire({
      title: "Delete Record?",
      icon: "warning",
      showCancelButton: true
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      if (type === "purchase") {
        await axiosInstance.delete(`/api/die-purchase/${id}`);
      } else {
        await axiosInstance.delete(`/api/die-discard/${id}`);
      }

      Swal.fire("Deleted", "Record removed", "success");
      if (activeTab === "purchase") {
        loadPurchase();
      } else if (activeTab === "discard") {
        loadDiscard();
      } else {
        loadSummary();
      }
    });
  };

  /* ================= TABLE COLUMNS ================= */

  const purchaseColumns = [
    { name: "Party", selector: row => row.PartyName },
    {
      name: "Date",
      selector: row => {
        if (!row.PurchaseDate) return "";
        const date = new Date(row.PurchaseDate);
        return date.toLocaleDateString("en-GB"); // 12/03/2026
      }
    },
    { name: "Die Code", selector: row => row.DieCode },
    { name: "Pieces", selector: row => row.NumberOfPieces },
    { name: "Amount", selector: row => row.Amount },
    {
      name: "Action",
      cell: row => (
        <>
          {canEdit && (
            <button
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => handleEdit(row)}
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row.PurchaseId, "purchase")}
            >
              Delete
            </button>
          )}
        </>
      )
    }
  ];

  const discardColumns = [
    { name: "Party", selector: row => row.PartyName },
    {
      name: "Date",
      selector: row => {
        if (!row.DiscardDate) return "";
        const date = new Date(row.DiscardDate);
        return date.toLocaleDateString("en-GB");
      }
    },
    { name: "Die Code", selector: row => row.DieCode },
    { name: "Pieces", selector: row => row.NumberOfPieces },
    {
      name: "Action",
      cell: row => (
        canDelete && (
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row.DiscardId, "discard")}
          >
            Delete
          </button>
        )
      )
    }
  ];

  const summaryColumns = [
    { name: "Die Code", selector: row => row.DieCode },
    { name: "Total Purchased", selector: row => row.TotalPurchased },
    { name: "Total Discarded", selector: row => row.TotalDiscarded },
    { name: "Available Stock", selector: row => row.AvailableStock }
  ];

  /* ================= UI ================= */

  return (
    <div className="d-flex dashboard">

      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4">

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3>Die Purchase Module</h3>

            {canAdd && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setFormType("purchase");
                    setShowModal(true);
                  }}
                >
                  + Die Purchase Entry
                </button>

                <button
                  className="btn btn-danger"
                  onClick={() => {
                    setFormType("discard");
                    setShowModal(true);
                  }}
                >
                  + Die Discard Entry
                </button>

                <button
                  className="btn btn-success"
                  onClick={handleExport}
                >
                  Export Excel
                </button>
              </div>
            )}
          </div>

          {/* TABS */}
          <ul className="nav nav-pills mb-3">
            <li className="nav-item">
              <button
                className={`nav-link d-flex align-items-center gap-2 ${activeTab === "summary" ? "active" : ""}`}
                onClick={() => setActiveTab("summary")}
              >
                {/* Inventory Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M2 2h12v3H2V2zm0 4h12v8H2V6zm2 2v4h2V8H4zm4 0v4h2V8H8z"/>
                </svg>
                Inventory Summary
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link d-flex align-items-center gap-2 ${activeTab === "purchase" ? "active" : ""}`}
                onClick={() => setActiveTab("purchase")}
              >
                {/* Purchase Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M0 1h2l.4 2H14l-1 5H3.2l-.2 1H13v2H2l-1-5H0V1z"/>
                </svg>
                Purchase List
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link d-flex align-items-center gap-2 ${activeTab === "discard" ? "active" : ""}`}
                onClick={() => setActiveTab("discard")}
              >
                {/* Discard Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M5.5 5.5l5 5m0-5l-5 5M2 3h12v1H2V3zm2 2h8l-1 9H5L4 5z"/>
                </svg>
                Discard List
              </button>
            </li>
          </ul>

          <div className="card shadow-sm">
            <div className="card-body">

              {activeTab === "summary" && (
                <>
                  <div className="mb-3">
                    <input
                      className="form-control"
                      style={{ width: 300 }}
                      placeholder="Search Die Code..."
                      value={summarySearch}
                      onChange={(e) => setSummarySearch(e.target.value)}
                    />
                  </div>

                  <DataTable
                    columns={summaryColumns}
                    data={summary}
                    pagination
                    progressPending={loading}
                  />
                </>
              )}

              {activeTab === "purchase" && (
                <>
                  <div className="d-flex justify-content-between mb-3">
                    <input
                      className="form-control"
                      style={{ width: 300 }}
                      placeholder="Search Party or Die Code..."
                      value={purchaseSearch}
                      onChange={(e) => setPurchaseSearch(e.target.value)}
                    />
                    <button className="btn btn-success" onClick={handleExport}>
                      Export Excel
                    </button>
                  </div>

                  <DataTable
                    columns={purchaseColumns}
                    data={purchaseList}
                    pagination
                    progressPending={loading}
                  />
                </>
              )}

              {activeTab === "discard" && (
                <>
                  <div className="mb-3">
                    <input
                      className="form-control"
                      style={{ width: 300 }}
                      placeholder="Search Party or Die Code..."
                      value={discardSearch}
                      onChange={(e) => setDiscardSearch(e.target.value)}
                    />
                  </div>

                  <DataTable
                    columns={discardColumns}
                    data={discardList}
                    pagination
                    progressPending={loading}
                  />
                </>
              )}

            </div>
          </div>

        </main>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show d-block">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>
                    {formType === "purchase"
                      ? editMode ? "Edit Purchase" : "Die Purchase Entry"
                      : "Die Discard Entry"}
                  </h5>
                  <button className="btn-close" onClick={resetForm}></button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="modal-body">

                    <div className="row">

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Party Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={formData.PartyName}
                          onChange={(e) =>
                            setFormData({ ...formData, PartyName: e.target.value })
                          }
                          required
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Die Code <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={formData.DieCode}
                          onChange={(e) =>
                            setFormData({ ...formData, DieCode: e.target.value })
                          }
                          required
                        />
                      </div>

                      {formType === "purchase" && (
                        <>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Purchase Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={formData.PurchaseDate}
                              onChange={(e) =>
                                setFormData({ ...formData, PurchaseDate: e.target.value })
                              }
                              required
                            />
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Number Of Pieces <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={formData.NumberOfPieces}
                              onChange={(e) =>
                                setFormData({ ...formData, NumberOfPieces: e.target.value })
                              }
                              required
                            />
                          </div>

                          <div className="col-md-12 mb-3">
                            <label className="form-label">Description</label>
                            <textarea
                              className="form-control"
                              value={formData.Description}
                              onChange={(e) =>
                                setFormData({ ...formData, Description: e.target.value })
                              }
                            />
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Amount <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={formData.Amount}
                              onChange={(e) =>
                                setFormData({ ...formData, Amount: e.target.value })
                              }
                              required
                            />
                          </div>
                        </>
                      )}

                      {formType === "discard" && (
                        <>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Discard Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={formData.DiscardDate}
                              onChange={(e) =>
                                setFormData({ ...formData, DiscardDate: e.target.value })
                              }
                              required
                            />
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Number Of Pieces <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={formData.NumberOfPieces}
                              onChange={(e) =>
                                setFormData({ ...formData, NumberOfPieces: e.target.value })
                              }
                              required
                            />
                          </div>
                        </>
                      )}

                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="submit" className="btn btn-success">
                      {editMode ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  </div>
                </form>

              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
